import { Image as ExpoImage } from 'expo-image';
import { router } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import {
  Bell,
  CarFront,
  Check,
  ChevronDown,
  CircleAlert,
  Flame,
  Frown,
  Heart,
  Laugh,
  MessageCircle,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  UserPlus,
  type LucideIcon,
} from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { LoadingView } from '@/src/components/LoadingView';
import { PressableScale } from '@/src/components/PressableScale';
import { RelativeTimeTooltip } from '@/src/components/RelativeTimeTooltip';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { getNotificationPostPath } from '@/src/features/notifications/notification-navigation';
import type {
  AppNotification,
  NotificationType,
} from '@/src/features/notifications/notification-service';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/src/features/notifications/use-notifications';
import { trackEvent } from '@/src/lib/analytics';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { isPermissionDeniedError } from '@/src/utils/errors';
import { openUserProfile } from '@/src/utils/profile-navigation';
import { useRefreshableListInteractions } from '@/src/utils/refreshable-list-interactions';
import { useRegisterTabScrollHandler } from '@/src/utils/tab-scroll';

type NotificationCategoryId =
  | 'all'
  | 'follows'
  | 'invites'
  | 'comments'
  | 'reactions'
  | 'mentions'
  | 'garage'
  | 'updates';

type NotificationCategory = {
  emptyText: string;
  emptyTitle: string;
  id: NotificationCategoryId;
  label: string;
  types?: NotificationType[];
};

type NotificationPresentation = {
  accessibilityLabel: string;
  badgeColor: string;
  Icon: LucideIcon;
  text: string;
};

const notificationCategories: NotificationCategory[] = [
  {
    id: 'all',
    label: 'All',
    emptyTitle: 'No notifications yet',
    emptyText:
      'Post reactions, comments, mentions, invites, follows, and product updates will appear here.',
  },
  {
    id: 'follows',
    label: 'Follows',
    types: ['profile_follow'],
    emptyTitle: 'No follow notifications',
    emptyText: 'New followers will appear here.',
  },
  {
    id: 'invites',
    label: 'Invites',
    types: ['invite_acceptance'],
    emptyTitle: 'No invite notifications',
    emptyText: 'Accepted invite signups will appear here.',
  },
  {
    id: 'comments',
    label: 'Comments',
    types: ['post_comment'],
    emptyTitle: 'No comment notifications',
    emptyText: 'Comments on your posts and comment threads will appear here.',
  },
  {
    id: 'reactions',
    label: 'Reactions',
    types: ['post_like', 'post_reaction', 'comment_reaction'],
    emptyTitle: 'No reaction notifications',
    emptyText: 'Post and comment reactions will appear here.',
  },
  {
    id: 'mentions',
    label: 'Mentions',
    types: ['post_mention', 'comment_mention'],
    emptyTitle: 'No mention notifications',
    emptyText: 'Post and comment mentions will appear here.',
  },
  {
    id: 'garage',
    label: 'Garage',
    types: [
      'vehicle_reaction',
      'vehicle_comment',
      'vehicle_comment_reaction',
      'vehicle_mention',
      'vehicle_comment_mention',
    ],
    emptyTitle: 'No Garage notifications',
    emptyText: 'Garage comments, mentions, and reactions will appear here.',
  },
  {
    id: 'updates',
    label: 'Updates',
    types: ['platform_update_digest'],
    emptyTitle: 'No product update notifications',
    emptyText: 'Frienzzi product updates appear here when enabled in Settings.',
  },
];

function notificationMatchesCategory(
  notification: AppNotification,
  category: NotificationCategory,
) {
  return !category.types || category.types.includes(notification.type);
}

const reactionNotificationBadgeColor = '#000000';
const reactionNotificationIcons: Record<
  NonNullable<AppNotification['reaction']>,
  LucideIcon
> = {
  love: Heart,
  like: ThumbsUp,
  dislike: ThumbsDown,
  laugh: Laugh,
  wow: CircleAlert,
  vomit: Frown,
  fire: Flame,
  trash: Trash2,
};

function reactionNotificationIcon(reaction: AppNotification['reaction']) {
  return reaction ? reactionNotificationIcons[reaction] : Heart;
}

function notificationPresentation(
  notification: AppNotification,
): NotificationPresentation {
  if (notification.type === 'platform_update_digest') {
    return {
      accessibilityLabel: 'System notification',
      badgeColor: colors.warmAccent,
      Icon: Bell,
      text: ' published a new transparency update.',
    };
  }

  if (notification.type === 'profile_follow') {
    return {
      accessibilityLabel: 'Follow notification',
      badgeColor: colors.accent,
      Icon: UserPlus,
      text: ' started following you.',
    };
  }

  if (notification.type === 'invite_acceptance') {
    return {
      accessibilityLabel: 'Invite notification',
      badgeColor: colors.primary,
      Icon: UserPlus,
      text: ' accepted your invite and joined Frienzzi.',
    };
  }

  if (notification.type === 'post_comment') {
    return {
      accessibilityLabel: 'Comment notification',
      badgeColor: colors.secondary,
      Icon: MessageCircle,
      text:
        notification.reason === 'comment_thread'
          ? ' commented on a post you commented on.'
          : ' commented on your post.',
    };
  }

  if (
    notification.type === 'vehicle_comment' ||
    notification.type === 'vehicle_mention' ||
    notification.type === 'vehicle_comment_mention'
  ) {
    return {
      accessibilityLabel: 'Garage notification',
      badgeColor: colors.secondary,
      Icon: CarFront,
      text:
        notification.type === 'vehicle_comment'
          ? ' commented on your Garage vehicle.'
          : notification.type === 'vehicle_comment_mention'
            ? ' mentioned you in a Garage comment.'
            : ' mentioned you on a Garage vehicle.',
    };
  }

  if (
    notification.type === 'post_mention' ||
    notification.type === 'comment_mention'
  ) {
    return {
      accessibilityLabel: 'Mention notification',
      badgeColor: colors.secondary,
      Icon: MessageCircle,
      text:
        notification.type === 'comment_mention'
          ? ' mentioned you in a comment.'
          : ' mentioned you in a post.',
    };
  }

  if (
    notification.type === 'post_reaction' ||
    notification.type === 'comment_reaction' ||
    notification.type === 'vehicle_reaction' ||
    notification.type === 'vehicle_comment_reaction'
  ) {
    return {
      accessibilityLabel: 'Reaction notification',
      badgeColor: reactionNotificationBadgeColor,
      Icon: reactionNotificationIcon(notification.reaction),
      text:
        notification.type === 'vehicle_reaction'
          ? ' reacted to your Garage vehicle.'
          : notification.type === 'vehicle_comment_reaction'
            ? ' reacted to your Garage comment.'
            : notification.type === 'comment_reaction'
              ? ' reacted to your comment.'
              : ' reacted to your post.',
    };
  }

  return {
    accessibilityLabel: 'Like notification',
    badgeColor: reactionNotificationBadgeColor,
    Icon: ThumbsUp,
    text: ' liked your post.',
  };
}

function NotificationItem({
  isRead,
  onActorPress,
  notification,
  onPress,
}: {
  isRead: boolean;
  notification: AppNotification;
  onActorPress: (notification: AppNotification) => void;
  onPress: (notification: AppNotification) => void;
}) {
  const isPlatformUpdate = notification.type === 'platform_update_digest';
  const actorName = isPlatformUpdate
    ? 'Frienzzi'
    : notification.actorFullName || 'Frienzzi user';
  const initial = actorName.slice(0, 1).toUpperCase();
  const unread = !isRead;
  const presentation = notificationPresentation(notification);
  const NotificationIcon = presentation.Icon;

  return (
    <PressableScale
      accessibilityLabel={`${presentation.accessibilityLabel}: ${actorName}${presentation.text}`}
      accessibilityRole="button"
      onPress={() => onPress(notification)}
      scaleTo={0.986}
      style={({ pressed }) => [
        styles.notification,
        unread ? styles.notificationUnread : null,
        pressed ? styles.notificationPressed : null,
      ]}
    >
      <View style={styles.avatarStack}>
        <Pressable
          accessibilityLabel={
            isPlatformUpdate
              ? 'Open system notification'
              : `View ${actorName}'s profile`
          }
          accessibilityRole="button"
          onPress={(event) => {
            event.stopPropagation();
            if (isPlatformUpdate) {
              onPress(notification);
              return;
            }
            onActorPress(notification);
          }}
          style={styles.avatar}
        >
          {notification.actorPhotoURL ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              source={{ uri: notification.actorPhotoURL }}
              style={styles.avatarImage}
              transition={120}
            />
          ) : (
            <Text style={styles.avatarText}>{initial}</Text>
          )}
        </Pressable>
        <View
          accessibilityElementsHidden
          importantForAccessibility="no"
          pointerEvents="none"
          style={[
            styles.notificationTypeBadge,
            { backgroundColor: presentation.badgeColor },
          ]}
        >
          <NotificationIcon color={colors.surface} size={12} strokeWidth={3} />
        </View>
      </View>
      <View style={styles.notificationBody}>
        <Text style={styles.notificationTitle}>
          <Text
            onPress={(event) => {
              event.stopPropagation();
              if (isPlatformUpdate) {
                onPress(notification);
                return;
              }
              onActorPress(notification);
            }}
            style={styles.actorName}
          >
            {actorName}
          </Text>
          {presentation.text}
        </Text>
        {notification.commentPreview ? (
          <Text style={styles.preview}>{notification.commentPreview}</Text>
        ) : notification.postPreview ? (
          <Text style={styles.preview}>{notification.postPreview}</Text>
        ) : notification.vehiclePreview ? (
          <Text style={styles.preview}>{notification.vehiclePreview}</Text>
        ) : null}
        <RelativeTimeTooltip
          accessibilityLabelPrefix="Notification received"
          textStyle={styles.date}
          tooltipStyle={styles.dateTooltip}
          value={notification.createdAt}
        />
      </View>
      {unread ? <View style={styles.unreadDot} /> : null}
    </PressableScale>
  );
}

export default function NotificationsScreen() {
  const { user } = useAuth();
  const isFocused = useIsFocused();
  const { showError, showSuccess } = useToast();
  const activeCategoryId = useAppStore(
    (state) => state.notificationTabPreference,
  );
  const setNotificationTabPreference = useAppStore(
    (state) => state.setNotificationTabPreference,
  );
  const notificationsState = useNotifications(user?.uid, undefined, isFocused);
  const hasMoreNotifications = notificationsState.hasMore;
  const loadMoreNotifications = notificationsState.loadMore;
  const notificationsLoading = notificationsState.loading;
  const notificationsLoadingMore = notificationsState.loadingMore;
  const notificationsRefreshing = notificationsState.refreshing;
  const markRead = useMarkNotificationRead(user?.uid);
  const markAllRead = useMarkAllNotificationsRead(user?.uid);
  const listRef = useRef<FlatList<AppNotification>>(null);
  const locallyReadIdsRef = useRef<Set<string>>(new Set());
  const [locallyReadIds, setLocallyReadIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const isNotificationRead = (notification: AppNotification) =>
    Boolean(notification.readAt) || locallyReadIds.has(notification.id);
  const hasUnread = notificationsState.notifications.some(
    (notification) => !isNotificationRead(notification),
  );
  const activeCategory =
    notificationCategories.find(
      (category) => category.id === activeCategoryId,
    ) ?? notificationCategories[0];
  const filteredNotifications = useMemo(
    () =>
      notificationsState.notifications.filter((notification) =>
        notificationMatchesCategory(notification, activeCategory),
      ),
    [activeCategory, notificationsState.notifications],
  );
  const isLookingForOlderCategoryNotifications =
    activeCategory.id !== 'all' &&
    filteredNotifications.length === 0 &&
    notificationsState.notifications.length > 0 &&
    hasMoreNotifications;
  const listInteractions = useRefreshableListInteractions({
    listRef,
    onRefresh: notificationsState.refresh,
    refreshing: notificationsState.refreshing,
  });

  useRegisterTabScrollHandler('notifications', listInteractions.scrollToTop);

  useEffect(() => {
    if (
      activeCategory.id === 'all' ||
      filteredNotifications.length > 0 ||
      notificationsLoading ||
      notificationsLoadingMore ||
      notificationsRefreshing ||
      !hasMoreNotifications ||
      notificationsState.notifications.length === 0
    ) {
      return;
    }

    loadMoreNotifications();
  }, [
    activeCategory.id,
    filteredNotifications.length,
    hasMoreNotifications,
    loadMoreNotifications,
    notificationsLoading,
    notificationsLoadingMore,
    notificationsState.notifications.length,
    notificationsRefreshing,
  ]);

  useEffect(() => {
    let changed = false;
    const nextIds = new Set(locallyReadIdsRef.current);

    notificationsState.notifications.forEach((notification) => {
      if (notification.readAt && nextIds.delete(notification.id)) {
        changed = true;
      }
    });

    if (changed) {
      locallyReadIdsRef.current = nextIds;
      setLocallyReadIds(nextIds);
    }
  }, [notificationsState.notifications]);

  if (
    notificationsState.loading &&
    notificationsState.notifications.length === 0
  ) {
    return <LoadingView message="Loading notifications..." />;
  }

  if (
    notificationsState.error &&
    notificationsState.notifications.length === 0
  ) {
    return (
      <Screen>
        <ErrorView message={notificationsState.error.message} />
      </Screen>
    );
  }

  function markNotificationReadOptimistically(notification: AppNotification) {
    if (notification.readAt || locallyReadIdsRef.current.has(notification.id)) {
      return;
    }

    const nextIds = new Set(locallyReadIdsRef.current);
    nextIds.add(notification.id);
    locallyReadIdsRef.current = nextIds;
    setLocallyReadIds(nextIds);

    markRead.mutate(notification.id, {
      onError: (error) => {
        if (isPermissionDeniedError(error)) {
          return;
        }

        const rollbackIds = new Set(locallyReadIdsRef.current);
        rollbackIds.delete(notification.id);
        locallyReadIdsRef.current = rollbackIds;
        setLocallyReadIds(rollbackIds);
        showError(error, 'Unable to mark that notification read.');
      },
    });
  }

  function selectNotificationCategory(category: NotificationCategory) {
    if (category.id === activeCategory.id) {
      setCategoryMenuVisible(false);
      return;
    }

    setNotificationTabPreference(category.id);
    setCategoryMenuVisible(false);
    listRef.current?.scrollToOffset({ animated: false, offset: 0 });
    trackEvent('notifications_category_select', {
      notification_category: category.id,
    });
  }

  function markLoadedNotificationsReadOptimistically() {
    const unreadIds = notificationsState.notifications
      .filter((notification) => !isNotificationRead(notification))
      .map((notification) => notification.id);

    if (unreadIds.length === 0) {
      return;
    }

    const previousIds = new Set(locallyReadIdsRef.current);
    const nextIds = new Set(previousIds);

    unreadIds.forEach((id) => nextIds.add(id));
    locallyReadIdsRef.current = nextIds;
    setLocallyReadIds(nextIds);

    markAllRead.mutate(undefined, {
      onError: (error) => {
        locallyReadIdsRef.current = previousIds;
        setLocallyReadIds(previousIds);
        showError(error, 'Unable to mark notifications read.');
      },
      onSuccess: () => {
        showSuccess('All notifications are marked read.');
      },
    });
    trackEvent('notifications_mark_all_read', {
      loaded_count: unreadIds.length,
    });
  }

  function openNotification(notification: AppNotification) {
    const wasUnread = !isNotificationRead(notification);
    markNotificationReadOptimistically(notification);

    trackEvent('notification_open', {
      notification_type: notification.type,
      was_unread: wasUnread,
    });

    if (
      notification.type === 'profile_follow' ||
      notification.type === 'invite_acceptance'
    ) {
      openUserProfile({
        currentUid: user?.uid,
        targetUid: notification.actorUid,
      });
      return;
    }

    if (notification.type === 'platform_update_digest') {
      router.push('/changelog');
      return;
    }

    if (notification.url?.startsWith('/garage/')) {
      router.push(notification.url);
      return;
    }

    const postPath = getNotificationPostPath(notification);

    if (postPath) {
      router.push(postPath);
    }
  }

  function openNotificationActor(notification: AppNotification) {
    markNotificationReadOptimistically(notification);

    openUserProfile({
      currentUid: user?.uid,
      targetUid: notification.actorUid,
    });
    trackEvent('profile_open', {
      source: 'notification_actor',
      target_is_current_user: notification.actorUid === user?.uid,
    });
  }

  return (
    <>
      <Screen scroll={false} contentStyle={styles.listScreenContent}>
        <FlatList
          ref={listRef}
          contentContainerStyle={styles.listContent}
          data={filteredNotifications}
          keyExtractor={(notification) => notification.id}
          ListHeaderComponent={
            <View style={styles.header}>
              <Pressable
                accessibilityLabel={`Notification filter: ${activeCategory.label}`}
                accessibilityRole="button"
                onPress={() => setCategoryMenuVisible(true)}
                style={({ pressed }) => [
                  styles.categorySelectButton,
                  pressed ? styles.categorySelectButtonPressed : null,
                ]}
              >
                <View style={styles.categorySelectText}>
                  <Text style={styles.categorySelectLabel}>Filter</Text>
                  <Text style={styles.categorySelectValue}>
                    {activeCategory.label}
                  </Text>
                </View>
                <ChevronDown
                  color={colors.secondary}
                  size={20}
                  strokeWidth={2.5}
                />
              </Pressable>
              {hasUnread ? (
                <Button
                  title="Mark read"
                  variant="secondary"
                  loading={markAllRead.isPending}
                  loadingTitle="Marking..."
                  onPress={markLoadedNotificationsReadOptimistically}
                  style={styles.markReadButton}
                />
              ) : null}
            </View>
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                {isLookingForOlderCategoryNotifications
                  ? `Looking for older ${activeCategory.label.toLowerCase()}...`
                  : activeCategory.emptyTitle}
              </Text>
              <Text style={styles.emptyText}>
                {isLookingForOlderCategoryNotifications
                  ? 'Checking your older notifications for this category.'
                  : activeCategory.emptyText}
              </Text>
            </View>
          }
          ListFooterComponent={
            notificationsState.loadingMore ? (
              <View style={styles.feedFooter}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
          onEndReached={notificationsState.loadMore}
          onEndReachedThreshold={0.4}
          onRefresh={notificationsState.refresh}
          onScroll={listInteractions.onScroll}
          refreshing={notificationsState.refreshing}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          {...listInteractions.webPullToRefreshHandlers}
          renderItem={({ item }) => (
            <NotificationItem
              isRead={isNotificationRead(item)}
              notification={item}
              onActorPress={openNotificationActor}
              onPress={openNotification}
            />
          )}
        />
      </Screen>
      <Modal
        animationType="fade"
        transparent
        visible={categoryMenuVisible}
        onRequestClose={() => setCategoryMenuVisible(false)}
      >
        <Pressable
          style={styles.categoryMenuBackdrop}
          onPress={() => setCategoryMenuVisible(false)}
        >
          <Pressable
            style={styles.categoryMenuSheet}
            onPress={(event) => event.stopPropagation()}
          >
            <Text style={styles.categoryMenuTitle}>Notification filter</Text>
            <View style={styles.categoryMenuOptions}>
              {notificationCategories.map((category) => {
                const selected = category.id === activeCategory.id;

                return (
                  <Pressable
                    key={category.id}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    onPress={() => selectNotificationCategory(category)}
                    style={({ pressed }) => [
                      styles.categoryMenuOption,
                      selected ? styles.categoryMenuOptionSelected : null,
                      pressed && !selected
                        ? styles.categoryMenuOptionPressed
                        : null,
                    ]}
                  >
                    <View style={styles.categoryMenuOptionText}>
                      <Text style={styles.categoryMenuOptionLabel}>
                        {category.label}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={styles.categoryMenuOptionDescription}
                      >
                        {category.emptyText}
                      </Text>
                    </View>
                    {selected ? (
                      <Check
                        color={colors.primary}
                        size={20}
                        strokeWidth={2.7}
                      />
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  listScreenContent: {
    alignSelf: 'stretch',
    maxWidth: '100%',
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    width: '100%',
  },
  listContent: {
    alignSelf: 'center',
    gap: spacing.md,
    maxWidth: 640,
    paddingBottom: 0,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    width: '100%',
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
    paddingBottom: spacing.xs,
  },
  categorySelectButton: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 52,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  categorySelectButtonPressed: {
    backgroundColor: colors.surfaceTint,
  },
  categorySelectText: {
    gap: 2,
  },
  categorySelectLabel: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  categorySelectValue: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '900',
  },
  categoryMenuBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(29, 15, 70, 0.42)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.xl,
  },
  categoryMenuSheet: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.lg,
    width: '100%',
  },
  categoryMenuTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  categoryMenuOptions: {
    gap: spacing.xs,
  },
  categoryMenuOption: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 56,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  categoryMenuOptionPressed: {
    backgroundColor: colors.surfaceTint,
  },
  categoryMenuOptionSelected: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.primary,
  },
  categoryMenuOptionText: {
    flex: 1,
    gap: 2,
  },
  categoryMenuOptionLabel: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  categoryMenuOptionDescription: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  markReadButton: {
    minHeight: 52,
    paddingHorizontal: spacing.md,
  },
  feedFooter: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  notification: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
  },
  notificationUnread: {
    borderColor: colors.primary,
  },
  notificationPressed: {
    opacity: 0.86,
  },
  avatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 44,
  },
  avatarStack: {
    height: 48,
    justifyContent: 'center',
    position: 'relative',
    width: 48,
  },
  avatarImage: {
    height: '100%',
    width: '100%',
  },
  avatarText: {
    color: colors.secondary,
    fontSize: 17,
    fontWeight: '900',
  },
  notificationTypeBadge: {
    alignItems: 'center',
    borderColor: colors.surface,
    borderRadius: 11,
    borderWidth: 2,
    bottom: 0,
    height: 22,
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    width: 22,
  },
  actorName: {
    color: colors.text,
    fontWeight: '900',
  },
  notificationBody: {
    flex: 1,
    gap: spacing.xs,
  },
  notificationTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 21,
  },
  preview: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  date: {
    color: colors.mutedText,
    fontSize: 12,
  },
  dateTooltip: {
    bottom: 22,
  },
  unreadDot: {
    backgroundColor: colors.primary,
    borderRadius: 5,
    height: 10,
    width: 10,
  },
  emptyState: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
});
